import { useCallback, useEffect, useRef, useState } from 'react';
import type { IncidentAssignment } from '../Types/IncidentAssignment';
import { getIncidentAssignments } from '../Services/IncidentService';

interface UseIncidentAssignmentsResult {
  assignments: IncidentAssignment[];
  loading: boolean;
  error: unknown;
  refetch: () => void;
}

export function useIncidentAssignments(
  incidentId: string | undefined,
): UseIncidentAssignmentsResult {
  const [assignments, setAssignments] = useState<IncidentAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const mountedRef = useRef(true);

  const fetch = useCallback(async () => {
    if (!incidentId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getIncidentAssignments(incidentId);
      if (mountedRef.current) {
        setAssignments(result.data?.assignments ?? []);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err);
        console.error('[useIncidentAssignments] Failed to load assignments', err);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [incidentId]);

  useEffect(() => {
    mountedRef.current = true;
    fetch();
    return () => {
      mountedRef.current = false;
    };
  }, [fetch]);

  return { assignments, loading, error, refetch: fetch };
}
